import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { message, notification } from "antd";

import { SuitabilityTestInvestor, SuitabilityTestPT, emptySuitabilityTestPT } from "../../Models";
import { IUseSuitabilityTestPT } from "./IUseSuitabilityTestPT";

export const useSuitabilityTestPT = ({
	suitabilityTestAPI,
	contractAPI,
	investorAPI,
	translations,
	unlockNextStep
}: IUseSuitabilityTestPT) => {
	const [searchParams] = useSearchParams();
	const [contractId, setContractId] = useState(0);
	const [investorInfo, setInvestorInfo] = useState<SuitabilityTestInvestor>({
		idInvestorTestFiller: undefined,
		name: undefined,
		surname: undefined,
		secondLastName: undefined,
		birthDate: undefined,
		dni: undefined,
	});
	const [testData, setTestData] = useState<SuitabilityTestPT>(emptySuitabilityTestPT);
	const [isSuitabilityDone, setIsSuitabilityDone] = useState(false);

	const t = (key: string, fallback: string) => translations[key] || fallback;

	const instrumentRows = useMemo(() => ([
		{ key: "deposits", label: t("suitabilityPtInstrumentDeposits", "Depósitos à ordem e/ou a prazo, certificados de aforro e de tesouro") },
		{ key: "moneyMarketFunds", label: t("suitabilityPtInstrumentMoneyMarketFunds", "Fundos do mercado monetário e de tesouraria") },
		{ key: "bonds", label: t("suitabilityPtInstrumentBonds", "Obrigações e fundos de investimento em obrigações") },
		{ key: "shares", label: t("suitabilityPtInstrumentShares", "Ações e fundos de ações") },
		{ key: "realEstateFunds", label: t("suitabilityPtInstrumentRealEstateFunds", "Fundos de investimento imobiliário") },
		{ key: "ventureCapitalFunds", label: t("suitabilityPtInstrumentVentureCapitalFunds", "Fundos de capital de risco") },
		{ key: "pensionFunds", label: t("suitabilityPtInstrumentPensionFunds", "Fundos de pensões e planos de poupança reforma") },
		{ key: "financialInsurance", label: t("suitabilityPtInstrumentFinancialInsurance", "Seguros financeiros") },
		{ key: "complexProducts", label: t("suitabilityPtInstrumentComplexProducts", "Outros - Produtos financeiros complexos") }
	]), [translations]);

	const knowledgeQuestions = useMemo(() => ([
		{
			key: "knowledgeQ1",
			title: t("suitabilityPtKnowledgeQ1Title", "Que tipo de instrumento é um fundo de investimento?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ1Option1", "É um produto financeiro") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ1Option2", "É uma apólice de seguro") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ1Option3", "É um imóvel") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ2",
			title: t("suitabilityPtKnowledgeQ2Title", "Um fundo de capital de risco:"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ2Option1", "Investe primordialmente em instrumentos derivados com finalidade especulativa") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ2Option2", "Investe apenas em instrumentos de capital alheio em sociedades com elevado potencial de desenvolvimento") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ2Option3", "Investe primordialmente em instrumentos de capital alheio unicamente com objetivos sociais e filantrópicos") },
				{ value: 4, label: t("suitabilityPtKnowledgeQ2Option4", "Investe primordialmente em instrumentos de capital próprio e de capital alheio em sociedades com elevado potencial de desenvolvimento") },
				{ value: 5, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ3",
			title: t("suitabilityPtKnowledgeQ3Title", "Que tipo de garantia oferecem os fundos de investimento?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ3Option1", "Usualmente oferecem garantias de capital e rendibilidade") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ3Option2", "Têm garantia porque a carteira é diversificada") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ3Option3", "Usualmente não oferecem garantias de capital nem de rendimento") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ4",
			title: t("suitabilityPtKnowledgeQ4Title", "Os fundos de obrigações oferecem rendimento fixo?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ4Option1", "Não. Depende da rendibilidade da carteira do fundo") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ4Option2", "Sim, mas apenas em fundos de distribuição") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ4Option3", "Sim, a qualquer momento a partir de €1") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ5",
			title: t("suitabilityPtKnowledgeQ5Title", "O que é um fundo de investimento alternativo fechado?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ5Option1", "É um fundo harmonizado da UE para retalho com reembolso a qualquer momento") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ5Option2", "É um fundo não harmonizado na UE em que, por regra, o resgate ocorre na liquidação") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ5Option3", "É um fundo em que o capital é variável") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ6",
			title: t("suitabilityPtKnowledgeQ6Title", "A que associa o risco de concentração?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ6Option1", "Excesso de gases com efeito de estufa no ambiente terrestre") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ6Option2", "Elevada exposição da carteira à mesma categoria de ativos ou geografia") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ6Option3", "Elevada exposição a ativos de baixa qualidade") },
				{ value: 4, label: t("suitabilityPtKnowledgeQ6Option4", "Volatilidade no preço dos ativos da carteira") },
				{ value: 5, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ7",
			title: t("suitabilityPtKnowledgeQ7Title", "Os ganhos obtidos com os investimentos podem ter origem:"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ7Option1", "Apenas na venda com mais-valia") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ7Option2", "Em juros, dividendos, cupões e mais-valias") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ7Option3", "Nas taxas de juros do mercado interbancário") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ8",
			title: t("suitabilityPtKnowledgeQ8Title", "Qual é a principal diferença entre um fundo de investimento não cotado e um ETF?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ8Option1", "Não há diferenças, são a mesma coisa") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ8Option2", "Os ETF são fundos não cotados") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ8Option3", "Os ETF negociam em mercado; os fundos não cotados não") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ9",
			title: t("suitabilityPtKnowledgeQ9Title", "A bolsa caiu 30% desde os máximos do ano passado. É certamente bom momento para investir."),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ9Option1", "Verdade: regressará necessariamente a esses níveis") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ9Option2", "Falso: isso não garante recuperação") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ9Option3", "Verdade: se puder esperar pelo menos cinco anos") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		},
		{
			key: "knowledgeQ10",
			title: t("suitabilityPtKnowledgeQ10Title", "Se as taxas de juro baixarem, o que acontece às cotações das obrigações já emitidas?"),
			options: [
				{ value: 1, label: t("suitabilityPtKnowledgeQ10Option1", "Perdem valor porque rendem menos do que as novas") },
				{ value: 2, label: t("suitabilityPtKnowledgeQ10Option2", "Podem subir, descer ou manter-se") },
				{ value: 3, label: t("suitabilityPtKnowledgeQ10Option3", "Sobem, porque os títulos antigos ficam mais atrativos") },
				{ value: 4, label: t("suitabilityPtNoAnswer", "Não sei / Não respondo") }
			]
		}
	]), [translations]);

	useEffect(() => {
		if (searchParams.has("ContractId")) {
			setContractId(Number(searchParams.get("ContractId")));
			return;
		}

		if (searchParams.has("hash")) {
			contractAPI.getContractByHash(searchParams.get("hash")!).then((res: any) => {
				if (res.success) {
					setContractId(Number(res.data));
				}
			});
		}
	}, [contractAPI, searchParams]);

	useEffect(() => {
		if (!contractId) {
			return;
		}

		suitabilityTestAPI.getSuitabilityTestPT(contractId).then(async (res: any) => {
			if (res.success && res.data?.idContract) {
				setTestData({
					...emptySuitabilityTestPT,
					...res.data,
					dateOfCompletion: res.data.dateOfCompletion
						? moment(res.data.dateOfCompletion).format("YYYY-MM-DD")
						: null,
				});
				setIsSuitabilityDone(true);
			} else {
				setTestData((prevState: SuitabilityTestPT) => ({
					...prevState,
					idContract: contractId,
					dateOfCompletion: prevState.dateOfCompletion ?? null,
				}));
			}

			await investorAPI.getInvestors(contractId).then((investorRes: any) => {
				if (investorRes.success && investorRes.data.length > 0) {
					setInvestorInfo({
						name: investorRes.data[0].name,
						surname: investorRes.data[0].lastName,
						secondLastName: investorRes.data[0].secondLastName,
						birthDate: investorRes.data[0].birthDate ? moment(investorRes.data[0].birthDate).format("YYYY-MM-DD") : undefined,
						dni: investorRes.data[0].dni || investorRes.data[0].nif || investorRes.data[0].passport || investorRes.data[0].residenceCard,
						idInvestorTestFiller: investorRes.data[0].id,
					});
				}
			});
		});
	}, [contractId, investorAPI, suitabilityTestAPI]);

	const isValid = useMemo(() => {
		if (isSuitabilityDone) {
			return true;
		}

		if (
			!testData.dateOfCompletion ||
			!testData.marketFollowFrequency ||
			!testData.educationLevel ||
			!testData.financialProfession ||
			!testData.financialServicesUsage
		) {
			return false;
		}

		for (const row of instrumentRows) {
			const hasTraded = testData[`${row.key}HasTraded`];
			if (hasTraded === null || hasTraded === undefined) {
				return false;
			}

			if (hasTraded === true && (!testData[`${row.key}FrequencyBand`] || !testData[`${row.key}AmountBand`])) {
				return false;
			}
		}

		for (let i = 1; i <= 10; i++) {
			if (!testData[`knowledgeQ${i}`]) {
				return false;
			}
		}

		return true;
	}, [instrumentRows, isSuitabilityDone, testData]);

	const updateField = (field: keyof SuitabilityTestPT, value: any) => {
		setTestData((currentState: SuitabilityTestPT) => ({
			...currentState,
			[field]: value
		}));
	};

	const saveTest = () => {
		if (!isValid) {
			notification.error({ message: t("suitabilityPtValidationError", "Debes completar todas las respuestas del test PT.") });
			return;
		}

		const payload: SuitabilityTestPT = {
			...testData,
			idContract: contractId,
			dateOfCompletion: testData.dateOfCompletion,
			idInvestorTestFiller: investorInfo.idInvestorTestFiller
		};

		suitabilityTestAPI.saveSuitabilityTestPT(payload).then((res: any) => {
			if (res.success) {
				setIsSuitabilityDone(true);
				unlockNextStep(contractId);
				message.success(translations.suitabilityTestSent);
			}
		});
	};

	return {
		t,
		contractId,
		investorInfo,
		testData,
		isSuitabilityDone,
		instrumentRows,
		knowledgeQuestions,
		updateField,
		saveTest
	};
};
